import Api from '../../../../../shared/Api';
import { Resolution, ICreateStitchedImageResponse } from '../Entities';
import { RoomTag } from '../roomTagging/view/ImageProcessingRoomTagging';

class ImageProcessingApi {
    static listImages(orderId: number, value: number): any {
        return new Api().get(`/views/orders/${orderId}/imageprocessing?isEdited=${value}`);
    }

    static getEditedImage(orderId: number, imageId: number): any {
        return new Api().get(`/views/orders/${orderId}/imageprocessing/${imageId}`);
    }

    static saveComment(orderId: number, imageId: number, text: string, recipients: number) {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/${imageId}/comment`, {
            text: text,
            recipients: recipients
        });
    }

    static saveNeedsEditing(orderId: number, imageId: number, needsEditing: boolean) {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/${imageId}/needsediting`, {
            needsEditing: needsEditing
        });
    }

    static downloadImages(orderId: number) {
        return new Api().get(`/views/orders/${orderId}/imageprocessing/download`);
    }

    static downloadEditedImages(orderId: number, isNormalResolution: boolean, isBroker: boolean) {
        return new Api().downloadFile(
            `/views/orders/${orderId}/imageprocessing/download?isEdited=1&isNormalResolution=${
                isNormalResolution ? 1 : 0
            }&isBroker=${isBroker ? 1 : 0}`
        );
    }

    static downloadImage(orderId: number, imageId: number) {
        return new Api().downloadFile(
            `/views/orders/${orderId}/imageprocessing/${imageId}/download`
        );
    }

    static downloadDNGImage(orderId: number, imageId: number) {
        return new Api().downloadDNGFile(
            `/views/orders/${orderId}/imageprocessing/${imageId}/download`
        );
    }

    static getImageProcessingInfo(orderId: number) {
        return new Api().get(`/views/orders/${orderId}/imageprocessing/info`);
    }

    static updateImageProcessingComment(orderId: number, text: string, type: number) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/comment`, {
            text: text,
            type: type
        });
    }

    static checkIfImageExistsOnOrder(orderId: number, filename: string, orderRowId: number) {
        return new Api().get(
            `/views/orders/${orderId}/imageprocessing/fileexists?filename=${filename}&orderRowId=${orderRowId}`
        );
    }

    static getCategories(orderId: number) {
        return new Api().get(`/views/orders/${orderId}/imageprocessing/categories`);
    }

    static updateCategory(orderId: number, imageId: number, category: number) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/${imageId}/category`, {
            category: category
        });
    }

    static swapCategories(
        orderId: number,
        id1: number,
        category1: number,
        id2: number,
        category2: number
    ) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/swap-categories`, {
            id1: id1,
            category1,
            id2: id2,
            category2
        });
    }

    static updateImagesCategory(orderId: number, imageIds: number[], category: any) {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/update-images-category`, {
            category: category.value,
            imageIds: imageIds
        });
    }

    static deleteMultiple(orderId: number, imageIds: number[]) {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/delete-multiple`, {
            imageIds: imageIds
        });
    }

    static returnMultiple(orderid: number, imageIds: number[], text: string, recipients: number) {
        return new Api().post(`/views/orders/${orderid}/imageprocessing/return-multipel`, {
            imageIds: imageIds,
            text: text,
            recipients: recipients
        });
    }

    static updateFilename(orderId: number, imageId: number, filename: string) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/${imageId}/filename`, {
            imageId: imageId,
            filename: filename
        });
    }

    static getPhotoStatus(orderId: number) {
        return new Api().get(`/views/orders/${orderId}/imageprocessing/photostatus`);
    }

    static doneEditingImages(orderId: number) {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/doneeditingimages`, '');
    }

    static deliverOrder(orderId: number, message: string, date: Date, deliverOnlyToMail: string) {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/deliver`, {
            deliveryMessage: message,
            deliveryDate: date,
            deliverOnlyToMail: deliverOnlyToMail
        });
    }

    static redeliverOrder(orderId: number) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/redeliver`);
    }

    static overrideScheduledDelivery(orderId: number) {
        return new Api().put(
            `/views/orders/${orderId}/imageprocessing/override-scheduled-delivery`
        );
    }

    static getDeliveryStatusForOrder(orderId: number) {
        return new Api().get(`/views/orders/${orderId}/imageprocessing/deliverystatus`);
    }

    static updateDelivery(orderId: number, message: string, date: Date, deliverOnlyToMail: string) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/deliver`, {
            deliveryMessage: message,
            deliveryDate: date,
            deliverOnlyToMail: deliverOnlyToMail
        });
    }

    static customerApproveImages(orderId: number, images: any[], extraImages: number) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/customerapproveimages`, {
            images: images,
            extraImages: extraImages
        });
    }

    static customerReapproveImages(orderId: number) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/customerreapproveimages`);
    }

    static reDeliverImages(orderId: number) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/redeliverimages`);
    }

    static resetDelivery(orderId: number) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/resetdelivery`, '');
    }

    static getZipDownloadLink(orderId: number, resolution: Resolution) {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/create-zip-link`, {
            type: resolution
        });
    }

    static emailImages(orderId: number, resolution: Resolution) {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/deliver/email`, {
            type: resolution
        });
    }

    static getImageGalleryReference(orderId: number) {
        return new Api().get(`/views/orders/${orderId}/imageprocessing/getimagegalleryreference`);
    }

    static getContentProvider(orderId: number) {
        return new Api().get(`/views/orders/${orderId}/imageprocessing/getcontentprovider`);
    }

    static createStitchedImage(
        leftStitchImageId: number,
        leftOffset: number,
        rightStitchImageId: number,
        rightOffset: number,
        orderId: number,
        orderRowId: number
    ): Promise<ICreateStitchedImageResponse> {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/createstitchedimage`, {
            leftStitchImageId: leftStitchImageId,
            leftStitchImageOffsetY: leftOffset,
            rightStitchImageId: rightStitchImageId,
            rightStitchImageOffsetY: rightOffset,
            orderRowId: orderRowId
        });
    }

    static deleteUneditedPhoto(photoId: number) {
        return new Api().delete('/views/photographer-uploads/' + photoId);
    }

    static deleteFailedUploadedPhoto(photoUploadMetadata: number) {
        return new Api().delete('/views/photographer-uploads/meta/' + photoUploadMetadata);
    }

    static saveMetaData(orderId: number, metadataList: any): any {
        return new Api().post('/views/photographer-uploads/meta?orderId=' + orderId, {
            metadataList: metadataList
        });
    }

    static photographerApprovePhotoOrder(photoOrderId: number) {
        return new Api().post(
            `/views/orders/${photoOrderId}/imageprocessing/photographer-approve`,
            {}
        );
    }

    static deleteEditedPhoto(photoOrderId: number, photoId: number) {
        return new Api().delete(
            `/views/orders/${photoOrderId}/imageprocessing/editedimage/${photoId}`
        );
    }

    static getVitecBrokerSystemDeliveryInfo(photoOrderId: number) {
        return new Api().get(`/views/orders/${photoOrderId}/imageprocessing/vitecbrokersystem`);
    }

    static uploadEditedImage(orderId: number, orderRowId: number, file: File) {
        return ImageProcessingApi.postFile(
            `/views/orders/${orderId}/imageprocessing/rows/${orderRowId}/edited`,
            file
        );
    }

    static replaceEditedImage(orderId: number, imageId: number, file: File) {
        return ImageProcessingApi.postFile(
            `/views/orders/${orderId}/imageprocessing?imageId=${imageId}`,
            file
        );
    }

    static uploadNormalImage(orderId: number, orderRowId: number, file: File) {
        return ImageProcessingApi.postFile(
            `/views/orders/${orderId}/imageprocessing/rows/${orderRowId}/normal`,
            file
        );
    }

    private static postFile(url: string, file: File): Promise<any> {
        let api = new Api();
        let formData = new FormData();
        formData.append('file', file, file.name);

        return api.executeRequest(() =>
            fetch(`${api.getServiceBase()}${url}`, {
                method: 'POST',
                body: formData,
                headers: api.appendHeaders()
            })
        );
    }

    static setImageInProgress(orderId: number, orderRowId: number, imageInfo: any) {
        return new Api().post(
            `/views/orders/${orderId}/imageprocessing/rows/${orderRowId}/edited/in-progress`,
            { ...imageInfo }
        );
    }

    static setNormalImageInProgress(orderId: number, orderRowId: number, imageInfo: any) {
        return new Api().post(
            `/views/orders/${orderId}/imageprocessing/rows/${orderRowId}/normal/in-progress`,
            { ...imageInfo }
        );
    }

    static downloadImageGroup(orderId: number, imageIds: number[]) {
        return new Api().downloadFile(`/views/orders/${orderId}/imageprocessing/download/group`, {
            images: imageIds
        });
    }

    static downloadMultipleExposures(orderId: number, photoIds: number[]) {
        return new Api().downloadFile(
            `/views/orders/${orderId}/imageprocessing/download-exposure`,
            { photoIds }
        );
    }

    static resetRejection(orderId: number) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/resetrejection`);
    }

    static saveImageAsFavorite(orderId: number, imageId: number) {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/${imageId}/favorite`, {});
    }

    static resetImageAsFavorite(orderId: number, imageId: number) {
        return new Api().delete(
            `/views/orders/${orderId}/imageprocessing/${imageId}/favorite/remove`
        );
    }

    static retrieveOrderAsPhotographer(orderId: number) {
        return new Api().put(`/views/orders/${orderId}/retrieve-order-as-photographer`);
    }

    static updateImagesSortOrder(orderId: number, sortOrder: any[]) {
        return new Api().put(`/views/orders/${orderId}/imageprocessing/update-images-sort-order`, {
            sortOrder
        });
    }

    static saveRoomTags(orderId: number, imageId: number, roomTags: RoomTag[]) {
        return new Api().post(`/views/orders/${orderId}/imageprocessing/${imageId}/roomTags`, {
            roomTags
        });
    }

    static deleteRoomTag(orderId: number, imageId: number, roomTag: number) {
        return new Api().delete(
            `/views/orders/${orderId}/imageprocessing/${imageId}/roomTag/${roomTag}`
        );
    }
}

export default ImageProcessingApi;
